import React, { useState, useEffect } from 'react';
import { messageApi } from '../services/api';
import { MessageTemplate } from '../types';
import './MessageConfig.css';

const MessageConfig: React.FC = () => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [messages, setMessages] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);

  // WhatsApp Service state
  const [whatsappConfig, setWhatsappConfig] = useState<{
    serviceUrl: string;
    configured: boolean;
  }>({
    serviceUrl: '',
    configured: false
  });
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('checking');

  const templateTypes = [
    {
      type: 'rtm_reminder',
      label: 'Recordatorio de RTM',
      description: 'Mensaje que se envía 2 días antes y el mismo día de vencimiento de la revisión técnico-mecánica a las 3 PM. Variables disponibles: {name}, {expiry_date}',
      defaultMessage: 'Hola {name}, te recordamos que tu revisión técnico-mecánica vence el {expiry_date}. Por favor, agenda tu cita lo antes posible.'
    },
    {
      type: 'birthday',
      label: 'Felicitación de Cumpleaños',
      description: 'Mensaje que se envía en el cumpleaños del cliente a las 3 PM. Variables disponibles: {name}',
      defaultMessage: '¡Feliz cumpleaños {name}! Esperamos que tengas un día maravilloso. 🎉🎂'
    }
  ];


  useEffect(() => {
    loadTemplates();
    loadWhatsAppConfig();
    checkConnectionStatus();
    
    // Auto-refresh status every 30 seconds to reduce performance impact
    const interval = setInterval(() => {
      checkConnectionStatus();
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Update messages when templates change
    const newMessages: { [key: string]: string } = {};
    templateTypes.forEach(templateType => {
      const template = templates.find(t => t.type === templateType.type);
      newMessages[templateType.type] = template?.message || templateType.defaultMessage;
    });
    setMessages(newMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const loadTemplates = async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await messageApi.getTemplates();
      setTemplates(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar las plantillas');
    } finally {
      setLoading(false);
    }
  };

  const loadWhatsAppConfig = async (): Promise<void> => {
    try {
      const config = await messageApi.getWhatsAppConfig();
      setWhatsappConfig(config);
    } catch (err: any) {
      console.error('Error loading WhatsApp config:', err);
      setWhatsappConfig({
        serviceUrl: 'http://127.0.0.1:5000',
        configured: false
      });
    }
  };

  const checkConnectionStatus = async (): Promise<void> => {
    try {
      const status = await messageApi.getWhatsAppStatus();
      setIsConnected(status.connected || false);
      
      if (status.connected) {
        setConnectionStatus('connected');
        setError(null);
      } else if (status.status === 'not_reachable') {
        setConnectionStatus('not_reachable');
        setError(status.message || 'El servicio de WhatsApp no está disponible');
      } else if (status.status === 'timeout') {
        setConnectionStatus('timeout');
        setError(status.message || 'Timeout al verificar el estado del servicio');
      } else {
        setConnectionStatus(status.status || 'error');
        setError(status.message || status.error || 'Error al verificar el estado del servicio');
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setIsConnected(false);
      setError(err.response?.data?.message || err.message || 'Error al verificar el estado del servicio');
    }
  };

  const handleUpdate = async (type: string, message: string): Promise<void> => {
    try {
      setSaving(prev => ({ ...prev, [type]: true }));
      await messageApi.updateTemplate(type, message);
      await loadTemplates();
      alert('Plantilla actualizada exitosamente');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al actualizar la plantilla');
    } finally {
      setSaving(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleMessageChange = (type: string, value: string): void => {
    setMessages(prev => ({ ...prev, [type]: value }));
  };


  if (loading) {
    return <div className="loading">Cargando plantillas...</div>;
  }

  return (
    <div className="message-config-container">
      {/* WhatsApp Service Status Section */}
      <div className="config-section">
        <h3>Estado del Servicio de WhatsApp</h3>
        <div className="whatsapp-connection-card">
          <div className="connection-status">
            <span className={`status-indicator ${connectionStatus}`}>
              {connectionStatus === 'connected' ? '✓ Conectado' :
               connectionStatus === 'not_reachable' ? '✗ Servicio no disponible' :
               connectionStatus === 'timeout' ? '⏱️ Timeout' :
               connectionStatus === 'checking' ? '? Verificando...' :
               '⚠️ Error'}
            </span>
            <button
              onClick={checkConnectionStatus}
              className="qr-button"
            >
              Actualizar Estado
            </button>
          </div>
          
          {error && (
            <div className="error-message" style={{ 
              padding: '1rem', 
              backgroundColor: '#f8d7da', 
              color: '#721c24', 
              borderRadius: '4px',
              marginTop: '1rem'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}
          
          {isConnected && (
            <div className="connected-message" style={{
              padding: '1rem',
              backgroundColor: '#d4edda',
              color: '#155724',
              borderRadius: '4px',
              marginTop: '1rem'
            }}>
              <p>✓ El servicio de WhatsApp está funcionando correctamente</p>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                <strong>URL del servicio:</strong> {whatsappConfig.serviceUrl}
              </p>
            </div>
          )}
          
          {!isConnected && connectionStatus === 'not_reachable' && (
            <div className="info-message" style={{ 
              padding: '1rem', 
              backgroundColor: '#fff3cd', 
              color: '#856404', 
              borderRadius: '4px',
              marginTop: '1rem'
            }}>
              <p><strong>⚠️ El servicio de WhatsApp no está disponible</strong></p>
              <p style={{ marginTop: '0.5rem' }}>
                El servicio se ejecuta automáticamente en el servidor. Si ves este mensaje, 
                contacta al administrador del sistema.
              </p>
              <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                El servicio debe estar corriendo en: <code>{whatsappConfig.serviceUrl}</code>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Message Templates Section */}
      <div className="config-section">
        <h3>Plantillas de Mensajes</h3>
        <p className="description">
          Configura los mensajes que se enviarán automáticamente a los clientes.
          <br />
          <strong>Recordatorios de RTM:</strong> Se envían 2 días antes y el mismo día de vencimiento a las 3:00 PM.
          <br />
          <strong>Cumpleaños:</strong> Se envían el día del cumpleaños a las 3:00 PM.
        </p>

        {templateTypes.map((templateType) => (
          <div key={templateType.type} className="template-card">
            <h4>{templateType.label}</h4>
            <p className="template-description">{templateType.description}</p>
            <textarea
              value={messages[templateType.type] || ''}
              onChange={(e) => handleMessageChange(templateType.type, e.target.value)}
              rows={4}
              className="template-input"
              disabled={saving[templateType.type]}
            />
            <button
              onClick={() => handleUpdate(templateType.type, messages[templateType.type] || '')}
              disabled={saving[templateType.type]}
              className="save-button"
            >
              {saving[templateType.type] ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MessageConfig;
